// Carousel.tsx
import React, { useRef, useEffect } from 'react';
import { Fade, Grid } from '@mui/material';
import PreviewCard from '../universal/cards/PreviewCard';
import { Upload, UploadTypeNames, User } from '../../workers/ApiWorker';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useCustomTheme } from '../../contexts/ThemeContext';
import LoadingPreviewCard from '../universal/cards/LoadingPreviewCard';
import { fadeTimeout } from '../../helpers/Themes';
import type { Swiper as SwiperType } from 'swiper';

interface CarouselProps {
    data: Upload[];
    slidesPerView: number;
    type: UploadTypeNames.CHARACTER | UploadTypeNames.ARENA;
    index: number;
    isMobile: boolean;
    user?: User;
}

const Carousel: React.FC<CarouselProps> = ({
    data,
    slidesPerView,
    type,
    index,
    isMobile,
    user,
}) => {
    const { theme } = useCustomTheme();
    const swiperRef = useRef<SwiperType | null>(null);
    const isLoading = data.length === 0;

    const calculatePadding = () => {
        return 0;
    };

    const padding = calculatePadding();

    // When data loads, reset swiper to first slide
    useEffect(() => {
        if (!isLoading && swiperRef.current) {
            swiperRef.current.slideTo(0, 0); // jump to slide 0 with no animation
        }
    }, [isLoading]);

    return (
        <Grid item xs={12} sx={{ marginBottom: 5 }}>
            <Fade in={true} timeout={fadeTimeout}>
                <div>
                    <>
                        <style>
                            {`
                .swiper-container {
                    position: relative;
                }

                .custom-prev-${index},
                .custom-next-${index} {
                    position: absolute;
                    top: 50%;
                    transform: translateY(-50%);
                    z-index: 10;
                    cursor: pointer;
                    font-size: 25px;
                    background-color: transparent;
                    border: none;
                    outline: none;
                    padding: 10px;
                    color: ${theme.palette.text.secondary};
                    transition: opacity 0.2s ease;
                }

                .custom-prev-${index} {
                    left: -50px;
                }

                .custom-next-${index} {
                    right: -60px;
                }

                .custom-prev-${index}:hover,
                .custom-next-${index}:hover {
                    opacity: 0.8;
                }

                .custom-prev-${index}.disabled,
                .custom-next-${index}.disabled {
                    opacity: 0.2;
                    cursor: default;
                    pointer-events: none;
                }

                .swiper-pagination {
                    display: none;
                }
                `}
                        </style>
                        <div className="swiper-container">
                            <Swiper
                                spaceBetween={30}
                                speed={1000}
                                navigation={{
                                    nextEl: `.custom-next-${index}`,
                                    prevEl: `.custom-prev-${index}`,
                                }}
                                modules={[Navigation]}
                                onSwiper={(swiper: SwiperType) => {
                                    swiperRef.current = swiper;
                                }}
                                breakpoints={{
                                    750: {
                                        slidesPerView: slidesPerView / 2,
                                        slidesPerGroup: slidesPerView / 2,
                                    },
                                    900: {
                                        slidesPerView:
                                            type === UploadTypeNames.ARENA
                                                ? slidesPerView
                                                : slidesPerView / 2,
                                        slidesPerGroup:
                                            type === UploadTypeNames.ARENA
                                                ? slidesPerView
                                                : slidesPerView / 2,
                                    },
                                    1020: {
                                        slidesPerView:
                                            type === UploadTypeNames.ARENA
                                                ? slidesPerView
                                                : slidesPerView - 1,
                                        slidesPerGroup:
                                            type === UploadTypeNames.ARENA
                                                ? slidesPerView
                                                : slidesPerView - 1,
                                    },
                                    1220: {
                                        slidesPerView:
                                            type === UploadTypeNames.ARENA
                                                ? slidesPerView
                                                : slidesPerView - 1,
                                        slidesPerGroup:
                                            type === UploadTypeNames.ARENA
                                                ? slidesPerView
                                                : slidesPerView - 1,
                                    },
                                    1340: {
                                        slidesPerView:
                                            type === UploadTypeNames.ARENA
                                                ? slidesPerView + 1
                                                : slidesPerView,
                                        slidesPerGroup:
                                            type === UploadTypeNames.ARENA
                                                ? slidesPerView + 1
                                                : slidesPerView,
                                    },
                                    1810: {
                                        slidesPerView:
                                            type === UploadTypeNames.ARENA
                                                ? slidesPerView + 1
                                                : slidesPerView + 1,
                                        slidesPerGroup:
                                            type === UploadTypeNames.ARENA
                                                ? slidesPerView + 1
                                                : slidesPerView + 1,
                                    },
                                    2335: {
                                        slidesPerView:
                                            type === UploadTypeNames.ARENA
                                                ? slidesPerView * 2
                                                : slidesPerView + 2,
                                        slidesPerGroup:
                                            type === UploadTypeNames.ARENA
                                                ? slidesPerView * 2
                                                : slidesPerView + 2,
                                    },
                                }}
                                onReachEnd={() => {}}
                            >
                                {data.length > 0
                                    ? data.map((upload) => (
                                          <SwiperSlide key={upload.uuid}>
                                              <Fade
                                                  in={true}
                                                  timeout={fadeTimeout}
                                              >
                                                  <Grid
                                                      item
                                                      sx={{
                                                          paddingLeft: padding,
                                                          paddingRight: padding,
                                                      }}
                                                  >
                                                      <PreviewCard
                                                          uuid={upload.uuid}
                                                          username={
                                                              upload.author
                                                                  .username
                                                          }
                                                          title={upload.name}
                                                          description={
                                                              upload.miniDescription
                                                          }
                                                          image={
                                                              upload.images.main
                                                          }
                                                          games={upload.games}
                                                          type={upload.type}
                                                          upload={upload}
                                                          user={user}
                                                          enableLink={true}
                                                      />
                                                  </Grid>
                                              </Fade>
                                          </SwiperSlide>
                                      ))
                                    : Array.from({ length: 10 }).map(
                                          (_, index) => (
                                              <SwiperSlide key={index}>
                                                  <Fade
                                                      in={true}
                                                      timeout={fadeTimeout}
                                                  >
                                                      <Grid
                                                          item
                                                          sx={{
                                                              paddingLeft:
                                                                  padding,
                                                              paddingRight:
                                                                  padding,
                                                          }}
                                                      >
                                                          <LoadingPreviewCard
                                                              invisible={false}
                                                          />
                                                      </Grid>
                                                  </Fade>
                                              </SwiperSlide>
                                          )
                                      )}
                            </Swiper>
                            <div>
                                <div
                                    className={`custom-prev-${index}${isLoading ? ' disabled' : ''}`}
                                >
                                    <ArrowBackIosIcon fontSize="inherit" />
                                </div>
                                <div
                                    className={`custom-next-${index}${isLoading ? ' disabled' : ''}`}
                                >
                                    <ArrowForwardIosIcon fontSize="inherit" />
                                </div>
                            </div>
                        </div>
                    </>
                </div>
            </Fade>
        </Grid>
    );
};

export default Carousel;
